import { faQuestionCircle } from "@fortawesome/pro-light-svg-icons";
import { faChevronRight, faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { darken } from "common/colors";
import { Hovercard } from "common/components/hovercard";
import { removeQueryStringParameter } from "common/helpers/removeQueryStringParameter";
import { last, take, first } from "lodash-es";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { Row, Col } from "antd";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import formatDistance from "date-fns/formatDistance";
import format from "date-fns/format";

import { Placeholder } from "common/components/placeholder";
import { request } from "common/components/request";
import { references } from "common/helpers/references";
import { Icon } from "common/components/core/icon";
import { Tooltip } from "common/components/tooltip";

// https://heap.io/wp-content/uploads/2020/02/Know_Your_Users@2x.png
export function SessionList({ data }) {
    return (
        <SessionListWrapper>
            {data.map(({ username, sessions }) => (
                <UserSessionWrapper key={username || "-"}>
                    <UsernameWrapper>
                        <Col className="user"><User username={username} /></Col>
                        <LastSeen offset={1}>{formatDistanceToNow(new Date(sessions[0].start))} ago</LastSeen>
                    </UsernameWrapper>
                    <Row wrap={false} css={`padding: ${(props) => props.theme.space(1.5)};`}>
                        <Col span={6}>
                            <UserInfo username={username} />
                        </Col>
                        <Col flex="auto">{sessions.map((session) => (
                            <Session key={session.sid} session={session} />
                        ))}
                        </Col>
                    </Row>
                </UserSessionWrapper>
            ))}
        </SessionListWrapper>
    );
}

function UserInfo({ username }) {
    const [userProps, setUserProps] = useState(null);
    const [status, setStatus] = useState("idle");
    
    useEffect(() => {
        async function fetchData() {
            setStatus("loading");
            if (username) {
                const { results } = await request("/users", {
                    data: {
                        sort: "last_login",
                        order: -1,
                        limit: 1,
                        name: username
                    }
                });
                setUserProps(results[0]);
            }
            setStatus("loaded");
        }
        
        fetchData();
    }, [username]);
    
    const getValueOrPlaceholder = (prop, formatter = (value) => value) => {
        if (status === "loaded") {
            if (!userProps) return "n/a";
            return formatter(userProps[prop]);
        }
        
        return <Placeholder noMargin />;
    };
    
    return (
        <UserInfoWrapper>
            <UserInfoRow>
                <strong>Username: </strong>
                <User username={username} />
            </UserInfoRow>
            <UserInfoRow>
                <strong>User ref: </strong>
                {getValueOrPlaceholder("_id")}
            </UserInfoRow>
            <UserInfoRow>
                <strong>Merchant: </strong>
                {getValueOrPlaceholder("merchant_ref", (value) => references.toAlias(value, "merchant"))}
            </UserInfoRow>
            <UserInfoRow>
                <strong>First name: </strong>
                {getValueOrPlaceholder("first_name")}
            </UserInfoRow>
            <UserInfoRow>
                <strong>Last name: </strong>
                {getValueOrPlaceholder("last_name")}
            </UserInfoRow>
            <UserInfoRow>
                <strong>Created: </strong>
                {getValueOrPlaceholder("created", (value) => format(new Date(value), "yyyy-MM-dd'T'HH:mm"))}
            </UserInfoRow>
        </UserInfoWrapper>
    );
}

function Session({ session }) {
    const [expanded, setExpanded] = useState(false);
    
    return (
        <SessionWrapper>
            <SessionHeader onClick={() => setExpanded(!expanded)}>
                <Col span={1}><Icon icon={expanded ? faChevronDown : faChevronRight} $shouldHover /></Col>
                <Col flex="auto">Session with <strong>{session.events.length} events</strong> ongoing for <strong>{formatDistance(new Date(last(session.events).ts), new Date(first(session.events).ts))}</strong></Col>
            </SessionHeader>
            <SessionEvents>
                <Col offset={1} flex="auto" css="display: flex; flex-wrap: wrap">
                    {(expanded ? session.events : take(session.events, 5)).map((event) => <Event event={event}/>)}
                </Col>
            </SessionEvents>
        </SessionWrapper>
    );
}

function User({ username }) {
    if (username) return username;
    
    return (
        <>
            Unknown user
            <Tooltip title="Usually indicates that the user hasn't authenticated with Pazien yet">
                <QuestionIcon $shouldHover />
            </Tooltip>
        </>
    );
}

function Event({ event }) {
    const tokenContent = () => {
        if (event.ev === "navigate") {
            return (
                <>
                    <strong>{event.ev}</strong>
                    <TokenExtra>{removeQueryStringParameter(event.to)}</TokenExtra>
                </>
            );
        }
        
        if (event.ev === "click") {
            return (
                <>
                    <strong>{event.ev}</strong>
                    <TokenExtra>{event.innerText}</TokenExtra>
                </>
            );
        }
        
        if (event.ev === "custom") {
            return (
                <strong>{event.name}</strong>
            );
        }
        
        return event.ev;
    };
    
    return (
        <Hovercard className="auto-width" header={`Type: ${event.ev}`} body={<HovercardBody event={event} />}>
            <EventBlock type={event.ev}>
                {tokenContent()}
            </EventBlock>
        </Hovercard>
    );
}

const TokenExtra = styled.span`
    font-family: ${(props) => props.theme.text.regular};
    color: ${(props) => props.theme.colors["gray-mid"]};
    padding-left: 5px;
    max-width: 100px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

function HovercardBody({ event }) {
    return (
        <div>
            {Object.entries(event).map(([key, value]) => (
                <Row wrap={false} key={key}>
                    <HoverKey>{key}: </HoverKey>
                    <Col flex="auto">{value}</Col>
                </Row>
            ))}
        </div>
    );
}

const HoverKey = styled(Col)`
    font-family: ${(props) => props.theme.text.medium};
    padding-right: 5px;
`;

const SessionEvents = styled(Row)`
    display: flex;
`;

const events = {
    click: "blue-soft",
    change: "orange-soft",
    navigate: "purple-soft",
    custom: "aqua-l-40"
};

const EventBlock = styled.div`
    height: 26px;
    padding: 0 5px;
    border: 1px solid ${(props) => darken(props.theme.colors[events[props.type] || "gray-soft"], 10)};
    margin-right: 5px;
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${(props) => props.theme.text.medium};
    font-size: ${(props) => props.theme.fontSizeMedium};
    border-radius: 3px;

    &:hover {
        cursor: pointer;
        ${(props) => `
            background-color: ${darken(props.theme.colors[events[props.type] || "gray-soft"], 5)};
        `}
    }

    ${(props) => `
        background-color: ${props.theme.colors[events[props.type] || "gray-soft"]};
    `}
`;

const UserInfoWrapper = styled.div`
    font-size: ${(props) => props.theme.fontSizeMedium};

    strong {
        font-family: ${(props) => props.theme.text.medium};
        margin-right: ${(props) => props.theme.space(0.25)};
    }
`;

const UserInfoRow = styled.div`
    margin-bottom: ${(props) => props.theme.space(0.25)};
    display: flex;
`;

const UserSessionWrapper = styled.div`
    border: 1px solid ${(props) => props.theme.colors["gray-soft"]};
    margin-bottom: ${(props) => props.theme.space(1.5)};
`;

const SessionWrapper = styled.div``;

const SessionHeader = styled(Row)`
    user-select: none;
    &:hover {
        cursor: pointer;

        ${Icon} {
            color: ${(props) => props.theme.colors["gray-dark"]};
        }
    }
`;

const SessionListWrapper = styled.div``;

const UsernameWrapper = styled(Row)`
    padding: ${(props) => props.theme.space(1.5)};
    border-bottom: 1px solid ${(props) => props.theme.colors["gray-soft"]};

    .user {
        font-size: ${(props) => props.theme.fontSizeExtraLarge};
    }
`;

const LastSeen = styled(Col)`
    font-size: ${(props) => props.theme.fontSizeLarge};
    color: ${(props) => props.theme.colors["gray-light"]};
    display: flex;
    align-items: center;
`;

const QuestionIcon = styled(Icon).attrs({
    icon: faQuestionCircle
})`
    margin-left: 5px;
    margin-right: 5px;
`;
